import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckCircle2, XCircle, ArrowLeft, Ban, Search, Target, Plane, Wrench, Clipboard } from 'lucide-react';
import PageShell from '../components/layout/PageShell';

const API_URL = process.env.REACT_APP_API_URL;

const STATUS_STYLES = {
  'Submitted':   { background: '#fff9db', color: '#e67700', border: '1px solid #ffe066' },
  'In Review':   { background: '#e8f4fd', color: '#1971c2', border: '1px solid #a5d8ff' },
  'In Progress': { background: '#ebfbee', color: '#2f9e44', border: '1px solid #b2f2bb' },
  'Completed':   { background: '#f3f0ff', color: '#5f3dc4', border: '1px solid #d0bfff' },
  'Cancelled':   { background: '#fff5f5', color: '#c92a2a', border: '1px solid #ffc9c9' },
};

const TYPE_ICON = {
  'Pre-Sales': Target, 'In-Flight Deal': Plane, 'Sold Deal': CheckCircle2, 'Break-Fix/Post-Sales': Wrench,
};

const STAGES = ['Submitted', 'In Review', 'In Progress', 'Completed'];

const STAGE_DESCRIPTIONS = {
  'Submitted':   'Your request has been received and logged. A RAAH team member will pick it up for review shortly.',
  'In Review':   'The RAAH team is reviewing the details of your request to confirm scope, priority, and the right people to assign.',
  'In Progress': 'Work is actively underway — your assigned team member is executing on the engagement.',
  'Completed':   'This engagement has been completed. Reach out if you need any follow-up support.',
};

const s = {
  backBtn:     { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#fd5f0d', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0, marginBottom: '20px' },
  headerRow:   { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' },
  title:       { fontSize: '18px', fontWeight: '700', color: '#111', margin: 0 },
  sub:         { fontSize: '12px', color: '#888', marginBottom: '8px' },
  statusBadge: (st) => ({ ...(STATUS_STYLES[st] || STATUS_STYLES['Submitted']), padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }),
  loading:     { textAlign: 'center', padding: '40px', color: '#888', fontSize: '14px' },
  error:       { display: 'flex', alignItems: 'center', gap: '6px', padding: '12px 16px', background: '#fdecea', border: '1px solid #f5c2be', borderRadius: '6px', color: '#b00020', fontSize: '13px' },
  notFound:    { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '60px 20px' },
  notFoundText:{ fontSize: '14px', color: '#888', marginBottom: '16px' },
  notFoundBtn: { padding: '8px 20px', background: '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },

  cancelledBanner: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: '#fff5f5', color: '#c92a2a', border: '1px solid #ffc9c9', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginTop: '20px' },
  trackScroll: { overflowX: 'auto', paddingBottom: '8px' },
  track:       { display: 'flex', alignItems: 'flex-start', minWidth: 'max-content', padding: '28px 8px 12px' },
  node:        (selected) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center', width: '150px', flexShrink: 0,
    padding: '8px 6px', borderRadius: '12px', cursor: 'pointer',
    background: selected ? '#fef0e6' : 'transparent',
    border: `1px solid ${selected ? '#fd5f0d' : 'transparent'}`,
    transition: 'background 0.15s, border-color 0.15s',
  }),
  descPanel:   { marginTop: '20px', padding: '16px 18px', background: '#fafafa', border: '1px solid #eee', borderRadius: '10px' },
  descTitle:   { fontSize: '13px', fontWeight: '700', color: '#111', marginBottom: '2px' },
  descMeta:    { fontSize: '11px', color: '#888', marginBottom: '8px' },
  descText:    { fontSize: '13px', color: '#555', lineHeight: '1.6' },
  circle: (state, cancelled) => ({
    width: state === 'current' ? '40px' : '32px',
    height: state === 'current' ? '40px' : '32px',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: state === 'done' ? (cancelled ? '#adb5bd' : '#1baf7a')
              : state === 'current' ? '#fd5f0d'
              : '#fff',
    border: state === 'upcoming' ? '2px solid #ddd' : 'none',
    color: '#fff',
    boxShadow: state === 'current' ? '0 0 0 6px rgba(253,95,13,0.15)' : 'none',
    transition: 'all 0.2s ease',
    flexShrink: 0,
  }),
  connector: (prevState, cancelled) => ({
    flex: 1, height: '3px', minWidth: '40px', marginTop: '16px',
    background: prevState === 'done' ? (cancelled ? '#adb5bd' : '#1baf7a') : '#e5e5e5',
  }),
  nodeLabel: (state) => ({ fontSize: '12px', fontWeight: state === 'current' ? '700' : '600', color: state === 'upcoming' ? '#aaa' : '#111', marginTop: '10px', textAlign: 'center' }),
  nodeTimestamp: (state) => ({ fontSize: '11px', color: state === 'upcoming' ? '#bbb' : '#888', marginTop: '2px', textAlign: 'center' }),
};

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Derives the visual state (done / current / upcoming) and a display timestamp for each
// forward stage. Cancelled is a terminal branch, not a 5th node: the track shows only
// whatever stages were actually reached (muted gray) plus a separate red banner.
function computePipeline(engagement) {
  const history = engagement.statusHistory || [];
  const isCancelled = engagement.status === 'Cancelled';

  const lastForwardEntry = [...history].reverse().find(h => STAGES.includes(h.status));
  const effectiveStatus = isCancelled ? (lastForwardEntry?.status || 'Submitted') : engagement.status;
  const currentIndex = STAGES.indexOf(effectiveStatus);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;

  const stages = STAGES.map((stageName, i) => {
    const historyEntry = history.find(h => h.status === stageName);
    let state;
    if (i < safeIndex) state = 'done';
    else if (i === safeIndex) state = (isCancelled || engagement.status === 'Completed') ? 'done' : 'current';
    else state = 'upcoming';

    // Engagements that predate statusHistory have sparse/empty history — fall back to
    // createdAt for Submitted and updatedAt for whatever stage it's currently sitting at,
    // since we don't know exactly when it transitioned into that stage.
    let timestamp = null;
    if (state !== 'upcoming') {
      if (historyEntry) timestamp = historyEntry.changedAt;
      else if (stageName === 'Submitted') timestamp = engagement.createdAt;
      else if (i === safeIndex) timestamp = engagement.updatedAt;
    }

    return { key: stageName, state, timestamp };
  });

  const cancelledAt = isCancelled
    ? (history.find(h => h.status === 'Cancelled')?.changedAt || engagement.updatedAt)
    : null;

  return { stages, isCancelled, cancelledAt };
}

// Picks a sensible stage to show details for on first load: the current stage,
// or — for Completed/Cancelled engagements with no "current" node — the last
// one actually reached.
function defaultStageKey(pipeline) {
  const current = pipeline.stages.find(st => st.state === 'current');
  if (current) return current.key;
  const reached = pipeline.stages.filter(st => st.state === 'done');
  return (reached[reached.length - 1] || pipeline.stages[0]).key;
}

function stageMetaLine(stage) {
  if (stage.state === 'upcoming') return 'Not started yet';
  if (stage.state === 'current') return `Current phase · started ${formatDateTime(stage.timestamp)}`;
  return `Completed ${formatDateTime(stage.timestamp)}`;
}

function PipelineTrack({ pipeline, selectedKey, onSelect }) {
  const { stages, isCancelled, cancelledAt } = pipeline;
  const selectedStage = stages.find(st => st.key === selectedKey) || stages[0];

  return (
    <div>
      <div style={s.trackScroll}>
        <div style={s.track}>
          {stages.map((stage, i) => (
            <React.Fragment key={stage.key}>
              {i > 0 && <div style={s.connector(stages[i - 1].state, isCancelled)} />}
              <div
                style={s.node(stage.key === selectedKey)}
                onClick={() => onSelect(stage.key)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(stage.key); } }}
              >
                <div style={s.circle(stage.state, isCancelled)}>
                  {stage.state === 'done' && <CheckCircle2 size={18} strokeWidth={2} />}
                </div>
                <div style={s.nodeLabel(stage.state)}>{stage.key}</div>
                <div style={s.nodeTimestamp(stage.state)}>
                  {stage.state === 'upcoming' ? 'Pending' : formatDateTime(stage.timestamp)}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      {isCancelled && (
        <div style={s.cancelledBanner}><Ban size={13} strokeWidth={2.5} /> Cancelled on {formatDateTime(cancelledAt)}</div>
      )}
      <div style={s.descPanel}>
        <div style={s.descTitle}>{selectedStage.key}</div>
        <div style={s.descMeta}>{stageMetaLine(selectedStage)}</div>
        <div style={s.descText}>{STAGE_DESCRIPTIONS[selectedStage.key]}</div>
      </div>
    </div>
  );
}

export default function TrackEngagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [notFound, setNotFound]     = useState(false);
  const [error, setError]           = useState('');
  const [selectedStage, setSelectedStage] = useState(null);

  useEffect(() => {
    async function fetchEngagement() {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
        });
        const res = await fetch(`${API_URL}api/profile/engagements/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 404) { setNotFound(true); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to fetch engagement.');
        setEngagement(data.engagement);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchEngagement();
  }, [id, getAccessTokenSilently]);

  return (
    <PageShell title="Track Engagement" desc="Live status of your engagement request.">
      <button style={s.backBtn} onClick={() => navigate('/my-requests')}>
        <ArrowLeft size={14} strokeWidth={2.5} /> Back to My Requests
      </button>

      {loading && <div style={s.loading}>Loading…</div>}
      {error && <div style={s.error}><XCircle size={14} strokeWidth={2} /> {error}</div>}

      {notFound && (
        <div style={s.notFound}>
          <Search size={40} strokeWidth={1.5} style={{ color: '#ccc', marginBottom: '12px' }} />
          <div style={s.notFoundText}>We couldn't find that engagement.</div>
          <button style={s.notFoundBtn} onClick={() => navigate('/my-requests')}>Back to My Requests</button>
        </div>
      )}

      {engagement && (
        <>
          <div style={s.headerRow}>
            {(() => { const TypeIcon = TYPE_ICON[engagement.engagementType] || Clipboard; return <TypeIcon size={22} strokeWidth={1.75} style={{ color: '#555' }} />; })()}
            <p style={s.title}>{engagement.accountName}</p>
          </div>
          <div style={s.sub}>
            Submitted {new Date(engagement.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            {' · '}
            <span style={s.statusBadge(engagement.status)}>{engagement.status}</span>
          </div>

          {(() => {
            const pipeline = computePipeline(engagement);
            const activeKey = selectedStage || defaultStageKey(pipeline);
            return <PipelineTrack pipeline={pipeline} selectedKey={activeKey} onSelect={setSelectedStage} />;
          })()}
        </>
      )}
    </PageShell>
  );
}
